import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

// P5.4: LastMoodSnapshot type definition
// {
//   mood: string | null,
//   stress: number | null (1-10),
//   energy: number | null (1-10),
//   trend: 'up' | 'down' | 'equal' | null,
//   streak: number,
//   ritual: string | null,
//   zpReward: number,
//   timestamp: string | null (ISO date)
// }
const MOOD_SNAPSHOT_STORAGE_KEY = 'mindflow_mood_snapshot_v1';
const TOKEN_STORAGE_KEY = 'token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // P0 Fix: Initialize states more robustly
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  
  // P5.4: Last mood snapshot for persistent dashboard hero
  const [lastMoodSnapshot, setLastMoodSnapshot] = useState(null);
  
  // Impersonation state - for admin viewing as another user
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  
  // Prevent double initialization
  const initRef = useRef(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // P0 Fix: Robust token validation and user fetch
  const initializeAuth = useCallback(async () => {
    // Prevent double initialization
    if (initRef.current) return;
    initRef.current = true;
    
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    console.log('🔐 Auth init - Token exists:', !!storedToken);
    
    if (!storedToken) {
      setLoading(false);
      setAuthInitialized(true);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      
      if (res.ok) {
        try {
          const userData = await res.json();
          setUser(userData);
          setToken(storedToken);
          console.log('🔐 Auth init - User loaded:', userData.first_name);
        } catch (jsonErr) {
          console.warn('🔐 Auth init - JSON parse error, clearing token');
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setToken(null);
          setUser(null);
        }
      } else {
        // Token invalid or expired - clear it
        console.warn('🔐 Auth init - Token invalid, clearing');
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('🔐 Auth init error:', error);
      // Network error - keep token, user will retry on next action
      // Don't logout on network errors to prevent accidental logouts
      setToken(storedToken);
    } finally {
      setLoading(false);
      setAuthInitialized(true);
    }
  }, [API_URL]);

  // P0 Fix: Single initialization on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // P5.4: Rehydrate lastMoodSnapshot from localStorage on mount
  useEffect(() => {
    try {
      const savedSnapshot = localStorage.getItem(MOOD_SNAPSHOT_STORAGE_KEY);
      if (savedSnapshot) {
        const parsed = JSON.parse(savedSnapshot);
        // Validate timestamp - clear if older than 24 hours
        if (parsed.timestamp) {
          const snapshotDate = new Date(parsed.timestamp);
          const now = new Date();
          const hoursDiff = (now - snapshotDate) / (1000 * 60 * 60);
          
          // Keep snapshot if within same day (24 hours)
          if (hoursDiff <= 24) {
            setLastMoodSnapshot(parsed);
            console.log('🧠 Mood snapshot rehydrated:', parsed.mood);
          } else {
            // Clear stale snapshot
            localStorage.removeItem(MOOD_SNAPSHOT_STORAGE_KEY);
            console.log('🧠 Stale mood snapshot cleared');
          }
        }
      }
    } catch (err) {
      console.error('Error rehydrating mood snapshot:', err);
      localStorage.removeItem(MOOD_SNAPSHOT_STORAGE_KEY);
    }
  }, []);

  // P0 Fix: Refetch user data (for profile updates, etc.)
  const refetchUser = useCallback(async () => {
    if (!token) return null;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        try {
          const userData = await res.json();
          setUser(userData);
          return userData;
        } catch {
          return null;
        }
      }
    } catch (error) {
      console.error('Refetch user error:', error);
    }
    return null;
  }, [API_URL, token]);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    if (!res.ok) {
      let errorMsg = 'Giriş başarısız';
      try {
        const error = await res.json();
        errorMsg = error.detail || errorMsg;
      } catch {
        errorMsg = `Sunucu hatası (${res.status})`;
      }
      throw new Error(errorMsg);
    }
    
    const data = await res.json();
    localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setAuthInitialized(true);
    console.log('🔐 Login successful:', data.user.first_name);
    return data;
  };

  const register = async (userData) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    
    if (!res.ok) {
      let errorMsg = 'Kayıt başarısız';
      try {
        const error = await res.json();
        errorMsg = error.detail || errorMsg;
      } catch {
        errorMsg = `Sunucu hatası (${res.status})`;
      }
      throw new Error(errorMsg);
    }
    
    const data = await res.json();
    localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setAuthInitialized(true);
    return data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    // P5.4: Clear mood snapshot on logout
    localStorage.removeItem(MOOD_SNAPSHOT_STORAGE_KEY);
    setLastMoodSnapshot(null);
    setToken(null);
    setUser(null);
    // Reset init ref to allow re-initialization after logout
    initRef.current = false;
    console.log('🔐 Logged out');
  }, []);

  // P5.4: Save last mood snapshot (persists to localStorage)
  const saveLastMoodSnapshot = (snapshot) => {
    if (!snapshot) {
      setLastMoodSnapshot(null);
      localStorage.removeItem(MOOD_SNAPSHOT_STORAGE_KEY);
      return;
    }
    
    const snapshotWithTimestamp = {
      ...snapshot,
      timestamp: snapshot.timestamp || new Date().toISOString(),
    };
    
    setLastMoodSnapshot(snapshotWithTimestamp);
    localStorage.setItem(MOOD_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshotWithTimestamp));
    console.log('🧠 Mood snapshot saved:', snapshotWithTimestamp.mood);
  };

  // P5.4: Update ritual in snapshot after completion
  const updateSnapshotRitual = (ritual, zpReward = 0) => {
    if (!lastMoodSnapshot) return;
    
    const updated = {
      ...lastMoodSnapshot,
      ritual,
      zpReward: (lastMoodSnapshot.zpReward || 0) + zpReward,
    };
    
    setLastMoodSnapshot(updated);
    localStorage.setItem(MOOD_SNAPSHOT_STORAGE_KEY, JSON.stringify(updated));
    console.log('🧠 Mood snapshot ritual updated:', ritual);
  };

  const updateProfile = async (updates) => {
    const res = await fetch(`${API_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });
    
    if (res.ok) {
      const updatedUser = await res.json();
      setUser(updatedUser);
      return updatedUser;
    }
    throw new Error('Profil güncellenemedi');
  };

  const isAdmin = user?.role === 'admin';
  const isPartner = user?.is_partner === true;
  const partnerId = user?.partner_id || null;
  const accessTier = user?.access_tier || 'none'; // none, masterclass, full, admin
  const hasPanelAccess = isAdmin || accessTier === 'masterclass' || accessTier === 'full' || accessTier === 'admin';

  // Impersonate a user (view their panel as admin)
  const impersonateUser = (targetUser) => {
    if (!isAdmin) return;
    setOriginalAdmin(user);
    setImpersonatedUser(targetUser);
    localStorage.setItem('impersonated_user', JSON.stringify(targetUser));
    localStorage.setItem('original_admin', JSON.stringify(user));
  };

  // Partner impersonation with token swap
  const impersonatePartner = (newToken, partnerUser, adminToken, adminUser) => {
    // Store original admin credentials for returning
    localStorage.setItem('admin_token_backup', adminToken);
    localStorage.setItem('admin_user_backup', JSON.stringify(adminUser));
    
    // Set new partner token and user
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setToken(newToken);
    setUser(partnerUser);
    setOriginalAdmin(adminUser);
    setImpersonatedUser(partnerUser);
    
    return true;
  };

  // Stop partner impersonation and return to admin
  const stopPartnerImpersonation = () => {
    const adminToken = localStorage.getItem('admin_token_backup');
    const adminUser = localStorage.getItem('admin_user_backup');
    
    if (adminToken && adminUser) {
      localStorage.setItem(TOKEN_STORAGE_KEY, adminToken);
      setToken(adminToken);
      setUser(JSON.parse(adminUser));
      
      // Cleanup backup storage
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_user_backup');
    }
    
    setImpersonatedUser(null);
    setOriginalAdmin(null);
    localStorage.removeItem('impersonated_user');
    localStorage.removeItem('original_admin');
    
    return true;
  };

  // Check if currently impersonating a partner
  const isImpersonatingPartner = !!localStorage.getItem('admin_token_backup');

  // Stop impersonation and return to admin
  const stopImpersonation = () => {
    // If impersonating partner with token swap, use that method
    if (isImpersonatingPartner) {
      return stopPartnerImpersonation();
    }
    
    setImpersonatedUser(null);
    setOriginalAdmin(null);
    localStorage.removeItem('impersonated_user');
    localStorage.removeItem('original_admin');
  };

  // Check for existing impersonation on mount
  useEffect(() => {
    const savedImpersonated = localStorage.getItem('impersonated_user');
    const savedAdmin = localStorage.getItem('original_admin');
    if (savedImpersonated && savedAdmin) {
      setImpersonatedUser(JSON.parse(savedImpersonated));
      setOriginalAdmin(JSON.parse(savedAdmin));
    }
  }, []);

  // The effective user (impersonated or real)
  const effectiveUser = impersonatedUser || user;
  const isImpersonating = !!impersonatedUser;

  return (
    <AuthContext.Provider value={{
      user,
      effectiveUser,
      token,
      loading,
      authInitialized,
      login,
      register,
      logout,
      refetchUser,
      updateProfile,
      isAdmin,
      isPartner,
      partnerId,
      accessTier,
      hasPanelAccess,
      isAuthenticated: !!user && !!token,
      // P5.4: Mood Snapshot for persistent dashboard hero
      lastMoodSnapshot,
      saveLastMoodSnapshot,
      updateSnapshotRitual,
      // Impersonation
      impersonateUser,
      impersonatePartner,
      stopImpersonation,
      stopPartnerImpersonation,
      isImpersonating,
      isImpersonatingPartner,
      impersonatedUser,
      originalAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};
